import { Injectable } from '@angular/core';
import { ApiResponse, Log, User } from "app/entities";
import { ProposalLog } from 'app/entities/proposal-log';
import { ApiService } from "app/services/api.service";
import { BehaviorSubject, Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';

export interface UserPreferences {
    hide_dashboard_values: boolean;
}
@Injectable({
    providedIn: 'root'
})
export class UsersService extends ApiService {
    private defaultPreferences: UserPreferences = { 
        hide_dashboard_values: false 
    };
    private userPreferencesSubject = new BehaviorSubject<UserPreferences>(this.defaultPreferences);
    public userPreferences$ = this.userPreferencesSubject.asObservable();

    findAll(params: any): Observable<User[]> {
        params = this.paramsFromObject(params);
        return this.httpService.get(`/v1/clients/users`, params).pipe(
            map(res => User.parse(res.data) as User[])
        )
    }

    simpleUsuariosAll() {
        return this.httpService.get(`/v1/clients/customer/simple-customers`, {}).pipe(
            map(res => User.parse(res.data) as User[])
        )
    }

    find(id: any) {
        return this.httpService.get(`/v1/clients/users/${id}`)
            .pipe(
                map(res => User.parse(res.data) as User)
            )
    }

    store(data: any) {
        let formData = this.formDataFromObject(data)
        return this.httpService.post(`/v1/clients/users`, formData)
            .pipe(
                map(res => User.parse(res.data) as User)
            )
    }

    update(id: number, data: any) {
        let formData = this.formDataFromObject(data)
        return this.httpService.post(`/v1/clients/users/${id}`, formData)
            .pipe(
                map(res => User.parse(res.data) as User)
            )
    }

    destroy(id: number) {
        return this.httpService.delete(`/v1/clients/users/${id}`)
    }

    requestNewPassword(id: number) {
        return this.httpService.post(`/v1/clients/password/${id}/request`)
    }

    changeCancellationStatus(id){
        return this.httpService.post(`/v1/clients/customer/${id}/cancel`, {})
    }

    changeRole(role, id){
        return this.httpService.post(`/v1/clients/customer/${id}/role`, {roleName: role})
    }


    findLogs(id, params) {
        return this.httpService.get(`/v1/clients/customer/${id}/logs`, params).pipe(
            map(res => ProposalLog.parse(res.data) as ProposalLog)
        )
    }

    getPreferences(id: number): Observable<UserPreferences> {
        return this.httpService.get(`/v1/clients/customer/${id}/preferences`).pipe(
            map(response => ({
                hide_dashboard_values: response.hide_dashboard_values
            } as UserPreferences)),
            catchError(error => {
                console.error('Error fetching dashboard visibility:', error);
                return of(this.defaultPreferences);
            })
        );
    }

    toggleDashboardValuesVisibility(id: number): Observable<UserPreferences> {
        return this.httpService.post(`/v1/clients/customer/${id}/dashboard-visibility`, {}).pipe(
            map(response => ({
                hide_dashboard_values: response.hide_dashboard_values
            } as UserPreferences)),
            catchError(error => {
                console.error('Error toggling dashboard visibility:', error);
                throw error;
            })
        );
    }

    getCurrentUserPreferencesState(): UserPreferences {
        return this.userPreferencesSubject.getValue();
    }

    updateUserPreferencesState(preferences: UserPreferences): void {
        this.userPreferencesSubject.next(preferences);
    }

    updateDashboardValuesVisibilityState(visibility: boolean): void {
        const currentPreferences = this.getCurrentUserPreferencesState();
        this.userPreferencesSubject.next({
            ...currentPreferences, 
            hide_dashboard_values: visibility,
        });
    }
}
